import { DataTableService } from 'src/app/shared/data-table/data-table.service';
import { LazyLoadEvent } from 'primeng/api/primeng-api';
import { PageOptions } from './../../../../../shared/data-table/data-table.interface';
import { CgpConfirmationDialogService } from './../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { BehaviorSubject } from 'rxjs';
import { EcmService } from './../../ecm.service';
import { ToastrService } from 'ngx-toastr';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Component, OnInit, Inject } from '@angular/core';
import { ColumnDef } from 'src/app/shared/data-table/data-table.interface';
import { filter, finalize } from 'rxjs/operators';
import { PartsByProgramComponent } from './parts-by-program/parts-by-program.component';
import { Router } from '@angular/router';

interface ToolPartRelationship {
  toolId: number;
  programId: number;
  partNumber: string;
  programNameYear: string;
  toolNo: string;
}

interface ToolPartRelationshipData {
  tool: ToolPartRelationship;
  readonly: boolean;
}

@Component({
  selector: 'tool-part-relationship',
  templateUrl: './tool-part-relationship.component.html',
  styleUrls: ['./tool-part-relationship.component.scss']
})
export class ToolPartRelationshipComponent implements OnInit {

  pageOptions: PageOptions = this.dataTableService.pageOptionsDefaultSort('toolId');

  resourced = false;
  searchBarInput: any;
  totalParts: number;
  readonly = false;
  tool: ToolPartRelationship;
  parts: any[];
  searchPartNumber: string;
  private necLecs: any[] = null;
  hasLoaded = false;
  loading = false;

  columns: ColumnDef[] = [
    { field: 'programNameYear', name: 'Program', sortingDisabled: true, editable: false, width: '12em' },
    { field: 'partNumber', name: 'Lead Part#', editable: false, width: '9em' },
    { field: 'partType', name: 'File Type', editable: false, width: '8em' },
    { field: 'partName', name: 'Part Name', editable: false, width: '18em' },
    { field: 'include', type: 'checkbox', sortingDisabled: true, name: 'Include', width: '6em', tooltip: 'Check this box to make relationship between Tool Number and Part Number' },
    { field: 'partNotInUse', type: 'checkbox', sortingDisabled: true, name: 'Part No Longer In Use', width: '12em', tooltip: (data: any) => data ? `Check this box to make this Part Number no longer in use for Tool# ${this.tool.toolNo}: You must also select a value under Hide After NEC LEC first.` : 'Check this box to make this Part Number no longer in use' },
    {
      field: 'necLecAfter', sortingDisabled: true, optionsListName: 'necLec', optionsListField: 'necLec', type: 'dropdown', placeHolderField: 'necLecAfter',
      name: 'Hide After NEC LEC', canEditDisplay: false, width: '12em', optionsList: this.necLecs, tooltip: 'You must select the NEC LEC to hide this Part Number after, if you have checked Part No Longer In Use.'
    },
  ];

  fieldEdit = {
    partNotInUse: (data: any) => {
      // Checkbox sets value after this function, so must update immediatly after
      setTimeout(() => data.partNotInUse = 0);

      if (!data.necLecAfter) {
        this.toastr.error('You must select an NEC LEC under Hide After NEC LEC before checking this box. Please try again.');
        return;
      }
      if (!data.partNotInUse) {
        this.toastr.error('Unchecking Part No Longer In Use will remove the NEC LEC under Hide After NEC LEC.');
      }
      data.necLecAfter = '';

      this.ecmService.updateToolPartRelations([{
        toolId: data.toolId,
        partId: data.partId,
        partToToolId: data.partToToolId,
        necLecAfter: ''
      }]).subscribe();
    },
    necLecAfter: (data: any) => {
      if (!data.necLecAfter) {
        return;
      }

      data.partNotInUse = 1;

      this.ecmService.updateToolPartRelations([{
        toolId: data.toolId,
        partId: data.partId,
        partToToolId: data.partToToolId,
        necLecAfter: data.necLecAfter
      }]).subscribe();
    },
    include: (data: any) => {
      this.confirmation.showConfirmationDialog({
        title: 'Confirm',
        message: `Are you sure you want to remove this part relationship from Tool# ${this.tool.toolNo}?`,
        onYesClick: () => this.removePartRelationship(data),
        onNoClick: () => {
          data.include = 1;
          this.confirmation.closeConfirmationDialog();
        }
      });
    }
  };

  constructor(
    private readonly router: Router,
    private readonly dialogRef: MatDialogRef<ToolPartRelationshipComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ToolPartRelationshipData,
    private readonly ecmService: EcmService,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog,
    private readonly confirmation: CgpConfirmationDialogService,
    private readonly dataTableService: DataTableService
  ) { }

  ngOnInit() {
    if (this.router.url === '/tprecm/ecm/re-source') {
      this.resourced = true;
      this.columns.forEach(val => { val.editable = false; val.headerHighlight = false; });
      this.columns.forEach(val => {
        if (val.field === 'programNameYear' || val.field === 'partNumber' || val.field === 'partType' ||
          val.field === 'partName') {
          return (val.type = 'readonly');
        }
      });
    }

    if (!this.validateDialogData()) {
      this.dialogRef.close();
      return;
    }

    this.tool = this.data.tool;
    this.readonly = this.data.readonly;

    this.ecmService.getNecLec(this.tool.toolId)
      .pipe(filter(Boolean))
      .subscribe((response: any) => {
        this.necLecs = (response.data ? [] : response)
          .map(nec => ({ necLec: nec }));

        this.columns[this.columns.findIndex(val => val.field === 'necLecAfter')].optionsList = this.necLecs;
      });

    if (this.readonly) {
      this.columns.forEach(col => col.editable = false);
    }
    this.hasLoaded = true;
  }

  onLazyLoad(event: LazyLoadEvent) {
    this.pageOptions.order = event.sortOrder === 1 ? 'asc' : 'desc';
    this.pageOptions.size = event.rows;
    this.pageOptions.sort = event.sortField;
    this.pageOptions.page = event.first / this.pageOptions.size;
    this.refreshToolPartRelations();
  }

  refreshToolPartRelations() {
    this.loading = true;
    this.ecmService.getToolPartRelations({
      toolId: this.tool.toolId,
      programId: this.tool.programId,
      toolNo: this.tool.toolNo,
      filterColumn: { partNumber: this.searchPartNumber }
    }, this.pageOptions).pipe(
      filter(Boolean),
      finalize(() => this.loading = false)).subscribe((data: { totalCount: number, data: any[] }) => {
        this.parts = data.data;
        this.totalParts = data.totalCount;
      });
  }

  rowEdit(event: any) {
    const callback = this.fieldEdit[event.field];
    if (callback != null) {
      callback(event.data);
    }
  }

  validateDialogData(): boolean {
    return this.data.tool.toolId != null;
  }

  removePartRelationship(data: any) {
    this.confirmation.closeConfirmationDialog();
    this.ecmService.deleteToolPartRelationship(data.partToToolId, data.partId, data.toolId)
      .subscribe(() => this.refreshToolPartRelations());
  }

  showPartsByProgram() {
    const dialogRef = this.dialog.open(PartsByProgramComponent, {
      disableClose: true,
      height: '54vh',
      width: '54vw',
      data: {

        tool: this.tool,
        allParts: false
      }
    });

    dialogRef.afterClosed().subscribe(() => this.refreshToolPartRelations());
  }

  showAllParts() {
    const data = {
      tool: { ...this.tool, programId: 0 },
      allParts: true
    };

    const dialogRef = this.dialog.open(PartsByProgramComponent, {
      disableClose: true,
      height: '54vh',
      width: '54vw',
      data
    });

    dialogRef.afterClosed().subscribe(() => this.refreshToolPartRelations());
  }

  close() {
    this.dialogRef.close(this.tool);
  }

  searchPartToolRelation(event: any) {
    this.searchPartNumber = event.value;
    this.refreshToolPartRelations();
  }

  clearSearch() {
    this.searchBarInput = '';
    this.searchPartNumber = '';
    this.refreshToolPartRelations();
  }


}
